import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { Tree } from 'antd';
import 'antd/lib/tree/style/index.css';
import React, { useEffect, useState } from 'react';
import api from '../../../../libs/api';
import { msalInstance } from '../../../auth';
import { AppThunk, RootState, useAppDispatch, useAppSelector } from '../../../core/store';
import { initiateFolders } from '../../../core/store/features/asite';
import { useToastify } from '../../../../Hooks';

const updateTreeData = (list: any, key: any, children: any) => {
    return list.map((node: any) => {
        if (node.key === key) {
            return { ...node, children };
        }

        if (node.children) {
            return { ...node, children: updateTreeData(node.children, key, children) };
        }

        return node;
    });
};

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: 216,
        flexGrow: 1,
        maxWidth: 400,
    },
    treeView: { height: 450, minHeight: 450, overflow: 'auto' },
    grid: {
        maxHeight: 457,
    },
    selectedFoldersTitle: {
        fontSize: '1.5em',
        paddingBottom: '1.5em',
    },
}));

const mapAsiteFoldersToTreeItem = (folders: any) => {
    return folders.map((folder: any) => ({
        title: folder.folderName,
        key: folder.folderId.toString(),
        isLeaf: !folder.hasChildren,
        disableCheckbox: foldersAlreadyOnSync?.some((el: any) => el == folder.folderId) ? true : false,
    }));
};

let currentNode: any = null;

let foldersAlreadyOnSync: any[] = [];

let apiCalled: boolean = false;
let treeDataSet: boolean = false;
let loadedFolders: any[] = [];

const FolderTree = ({
    setFoldersToAdd,
    allLoadedAsiteFolders,
    setAllLoadedAsiteFolders,
    singleSelect,
    setSelectedPath,
}: any) => {
    const initState = useAppSelector((state: RootState) => state.asite);
    const [id, setId] = useState<string | undefined>();
    const [applicationId, setApplicationId] = useState();

    const classes = useStyles();
    const dispatch = useAppDispatch();

    const [treeData, setTreeData] = useState(mapAsiteFoldersToTreeItem(initState.data));

    const { showToastError } = useToastify();

    const getSettings = (): AppThunk => async (_dispatch: any, getState: any) => {
        const applicationId = getState().asite.applicationId;
        const id = msalInstance.getActiveAccount()?.localAccountId!;

        setId(id);
        setApplicationId(applicationId);
        return true;
    };

    useEffect(() => {
        const getFolders = async () => {
            await api
                .get('/api/v2/AsiteRelaticsSync/GetIdsConfigurationFolders', {
                    safe: true,
                })
                .then((res: any) => (foldersAlreadyOnSync = res))
                .catch((err) => console.log(err));
        };
        if (id === undefined && applicationId === undefined) {
            dispatch(getSettings());
            getFolders()
                .then((res: any) => {
                    dispatch(initiateFolders());
                })
                .catch((err) => console.log(err));
        }
    });

    const onCheck = ({ checked }: any) => {
        let selectedFolders: any = [];
        checked.forEach((element: any) => {
            const res = getNodeInfoByFolderIdsString(element);
            selectedFolders.push(res);
        });

        selectedFolders.forEach((parent: any) => {
            selectedFolders = selectedFolders.filter((r: any) => r === parent || !r.fullPath.includes(parent.fullPath));
        });

        setFoldersToAdd(selectedFolders);
    };

    const findItemByFoldersKey = (folderIdArray: any, asiteFolderTree: any) => {
        if (currentNode) return;

        asiteFolderTree.forEach((asiteFolderTreeNode: any) => {
            if (asiteFolderTreeNode.key == folderIdArray.toString()) {
                currentNode = asiteFolderTreeNode;
                return;
            }

            if (asiteFolderTreeNode.children) {
                findItemByFoldersKey(folderIdArray, asiteFolderTreeNode.children);
            }
        });
    };

    const callGetAsiteFoldersTreeByIdParent = async (folderId: string) => {
        return await api
            .get('/api/Asite/GetAsiteFoldersTreeByIdParent', {
                safe: true,
                params: { id, folderId, applicationId },
            })
            .then((response: any) => {
                return response;
            })
            .catch(() => {
                showToastError(`Error during fetch folders.`);
            });
    };
    const onLoadData = ({ key, children, disableCheckbox }: any) => {
        return new Promise<void>((resolve, reject) => {
            if (children) {
                resolve();
                return;
            }

            const keys = (key + '').split('-');
            if (keys.length < 1) {
                resolve();
                return;
            }

            const folderId = keys[keys.length - 1];
            if (!apiCalled) {
                loadedFolders = [];
                apiCalled = true;
                callGetAsiteFoldersTreeByIdParent(folderId)
                    .then((result) => {
                        loadedFolders = result;
                        return (
                            setTreeData((origin: any) => {
                                setAllLoadedAsiteFolders(
                                    allLoadedAsiteFolders.concat(loadedFolders.slice(0, loadedFolders.length)),
                                );
                                const children = mapAsiteFoldersToTreeItem(loadedFolders);
                                children.forEach((node: any) => {
                                    node.key = `${key}-${node.key}`;
                                    node.disableCheckbox = node.disableCheckbox
                                        ? node.disableCheckbox
                                        : disableCheckbox;
                                });
                                return updateTreeData(origin, key, children);
                            }),
                            resolve()
                        );
                    })
                    .catch((e) => {
                        showToastError(`Error during fetch folders.`);
                        reject(e);
                    });
            }

            if (treeDataSet === false) {
                treeDataSet = true;
                return;
            }
        });
    };

    const onExpand = () => {
        apiCalled = false;
        treeDataSet = false;
    };

    useEffect(() => {
        setAllLoadedAsiteFolders(allLoadedAsiteFolders.concat(initState.data));
        setTreeData(mapAsiteFoldersToTreeItem(initState.data));
    }, [initState]);

    function getNodeInfoByFolderIdsString(element: string) {
        let folderToSearch: any = '';
        let fullPath: any = '';
        let workspaceName = '';
        currentNode = null;
        const folderPathElements = element.split('-');
        folderPathElements.forEach((folderId: any) => {
            currentNode = null;
            folderToSearch = folderToSearch ? `${folderToSearch}-${folderId}` : `${folderId}`;
            findItemByFoldersKey(folderToSearch, treeData);
            if (workspaceName == '') {
                workspaceName = currentNode.title;
            }
            fullPath = fullPath ? `${fullPath}/${currentNode.title}` : `${currentNode.title}`;
        });
        const folderId = currentNode.key.split('-')[currentNode.key.split('-').length - 1];
        const res = { workspaceName, fullPath, folderId, folderName: currentNode.title };
        return res;
    }

    return (
        <div style={{ width: '50%', minHeight: 480, maxHeight: 480 }}>
            <span className={classes.selectedFoldersTitle} style={{ marginBottom: 15 }}>
                Folders tree
            </span>
            {treeData.length > 0 ? (
                <div style={{ maxHeight: 480, overflowY: singleSelect ? 'unset' : 'auto', paddingTop: '3%' }}>
                    <Tree
                        checkStrictly
                        checkable={singleSelect ? false : true}
                        loadData={onLoadData}
                        treeData={treeData}
                        onCheck={onCheck}
                        onExpand={onExpand}
                        onSelect={(props) => {
                            setSelectedPath(getNodeInfoByFolderIdsString(props.toString()));
                        }}
                        selectable={singleSelect ? true : false}
                    />
                </div>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                    <CircularProgress />
                </div>
            )}
        </div>
    );
};

export default FolderTree;
